import toast from 'react-hot-toast';

const API_URL = (import.meta.env.MODE === 'production') ? '/api' : (import.meta.env.VITE_API_URL || '/api');

export class ApiResponseError extends Error {
    constructor(message, status, data) {
        super(message);
        this.name = 'ApiResponseError';
        this.status = status;
        this.data = data;
    }
}

export const AUTH_EXPIRED_EVENT = 'postpaid:auth-expired';
let authExpiredNotified = false;

function notifyAuthExpired() {
    if (authExpiredNotified || typeof window === 'undefined') return;
    authExpiredNotified = true;
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}

/**
 * Get CSRF token from cookie
 */
function getCsrfToken() {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'csrfToken') {
            return value;
        }
    }
    return null;
}

const REQUEST_TIMEOUT_MS = 15000;
const RETRY_DELAY_MS = 500;
const RETRYABLE_STATUS = new Set([502, 503, 504]);

function extractErrorMessage(errorData) {
    if (typeof errorData?.error === 'string' && errorData.error) return errorData.error;
    const details = errorData?.details;
    if (Array.isArray(details) && details.length > 0) {
        const first = details[0];
        if (typeof first === 'string') return first;
        if (first && typeof first.message === 'string') return first.message;
    }
    if (typeof errorData?.message === 'string' && errorData.message) return errorData.message;
    return 'API request failed';
}

async function request(endpoint, options = {}) {
    const { suppressErrorToast = false, retry = true, ...fetchOptions } = options;
    const defaultHeaders = {
        'Content-Type': 'application/json',
    };

    const method = (fetchOptions.method || 'GET').toUpperCase();
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        const csrfToken = getCsrfToken();
        if (csrfToken) {
            defaultHeaders['X-CSRF-Token'] = csrfToken;
        }
    }

    const config = {
        ...fetchOptions,
        headers: {
            ...defaultHeaders,
            ...fetchOptions.headers,
        },
        credentials: 'include',
    };

    // Only retry idempotent GETs — retrying non-idempotent methods could
    // double-charge users or duplicate writes.
    const maxAttempts = method === 'GET' && retry ? 2 : 1;
    let lastError = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

        try {
            const response = await fetch(`${API_URL}${endpoint}`, {
                ...config,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);

            if (!response) {
                throw lastError || new Error('No response received');
            }

            if (RETRYABLE_STATUS.has(response.status) && attempt < maxAttempts) {
                await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
                continue;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = extractErrorMessage(errorData);

                if (response.status === 401) {
                    notifyAuthExpired();
                    // Suppress 401 alarms — AuthContext handles the sign-out flow
                } else if (!suppressErrorToast) {
                    toast.error(errorMessage);
                }

                throw new ApiResponseError(errorMessage, response.status, errorData);
            }

            if (response.status === 204) return null;
            return response.json();
        } catch (error) {
            clearTimeout(timeoutId);
            lastError = error;

            if (error instanceof ApiResponseError) throw error;

            // Network failure or timeout — retry GETs once before giving up
            if (attempt < maxAttempts) {
                await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
                continue;
            }

            const isTimeout = error.name === 'AbortError';
            if (!suppressErrorToast) {
                toast.error(isTimeout ? 'Request timed out — please try again' : `Connection error: ${error.message}`);
            }
            throw error;
        }
    }

    throw lastError;
}

// Module-level cache for getStateMachine
let _stateMachineCache = null;

export const api = {
    // Auth
    auth: {
        register: async (data) => {
            const response = await request('/register', { method: 'POST', body: JSON.stringify(data) });
            authExpiredNotified = false;
            return response;
        },
        login: async (data) => {
            const response = await request('/login', { method: 'POST', body: JSON.stringify(data) });
            authExpiredNotified = false;
            return response;
        },
        logout: () => request('/logout', { method: 'POST' }),
        getMe: async () => request('/me'),
        forgotPassword: (email, captchaToken) => request('/forgot-password', { method: 'POST', body: JSON.stringify({ email, captchaToken }) }),
        resetPassword: (data) => request('/reset-password', { method: 'POST', body: JSON.stringify(data) }),
        sendVerification: () => request('/send-verification', { method: 'POST' }),
        verifyEmail: (code) => request('/verify-email', { method: 'POST', body: JSON.stringify({ code }) }),
    },

    // User Profile
    // User Profile
    updateProfile: (data) => request('/users/me', { method: 'PUT', body: JSON.stringify(data) }),
    getInfluencers: () => request('/users/influencers'),
    getAdminUsers: () => request('/users/admin'),
    updateUserStatus: (id, status) => request(`/users/admin/users/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    updateUserBalance: (id, amount) => request(`/users/admin/users/${id}/balance`, { method: 'PATCH', body: JSON.stringify({ amount }) }),
    verifyUser: (id) => request(`/users/admin/users/${id}/verify`, { method: 'PATCH' }),
    deleteUser: (id) => request(`/users/admin/users/${id}`, { method: 'DELETE' }),
    getMyReferrals: () => request('/users/referrals/me'),

    // Admin Payouts
    admin: {
        getPayouts: () => request('/admin/payouts'),
        releasePayout: (id, amount) => request(`/admin/payouts/${id}/release`, { method: 'POST', body: JSON.stringify({ amount: amount ? Math.round(parseFloat(amount) * 100) : undefined }) }),
        syncPayoutMetrics: (id) => request(`/admin/payouts/${id}/sync`, { method: 'POST' }),
        getAuditLogs: () => request('/admin/audit-logs'),
        support: {
            listChats: () => request('/support/admin/chats'),
            createChat: (data) => request('/support/admin/chats', { method: 'POST', body: JSON.stringify(data) }),
            getChat: (id) => request(`/support/admin/chats/${id}`),
            sendMessage: (id, body) => request(`/support/admin/chats/${id}/messages`, { method: 'POST', body: JSON.stringify({ body }) }),
            sendWidgetMessage: (id, body) => request(`/support/admin/widget/${id}/messages`, { method: 'POST', body: JSON.stringify({ body }) }),
            updateStatus: (id, status) => request(`/support/admin/chats/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
        },
    },

    // Social Accounts
    social: {
        link: (data) => request('/social/link', { method: 'POST', body: JSON.stringify(data) }),
        get: () => request('/social'),
        getPending: () => request('/social/admin/pending'),
        verifyMock: (data) => request('/social/verify-mock', { method: 'POST', body: JSON.stringify(data) }),
        devVerify: (data) => request('/social/dev-verify', { method: 'POST', body: JSON.stringify(data) }),
        verifyAdmin: (accountId, action) => request('/social/admin/verify', { method: 'POST', body: JSON.stringify({ accountId, action }) }),
        initiateVerification: (data) => request('/social/initiate-verification', { method: 'POST', body: JSON.stringify(data) }),
        verify: (data) => request('/social/verify', { method: 'POST', body: JSON.stringify(data) }),
        regenerateCode: (data) => request('/social/regenerate-code', { method: 'POST', body: JSON.stringify(data) }),
        getVerificationStatus: (accountId) => request(`/social/verification-status/${accountId}`),
        preview: (data) => request('/social/preview', { method: 'POST', body: JSON.stringify(data) }),
    },

    // Campaigns
    getCampaigns: () => request('/campaigns'),
};

const toCents = (val) => Math.round((parseFloat(val) || 0) * 100);

const processCampaignData = (data) => {
    const processed = {
        ...data,
        budget: toCents(data.budget),
        makingBonus: toCents(data.makingBonus),
        pricePerLike: toCents(data.pricePerLike),
        pricePerComment: toCents(data.pricePerComment),
        pricePerShare: toCents(data.pricePerShare),
        pricePerView: toCents(data.pricePerView),
    };

    if (data.platforms) {
        processed.platforms = data.platforms.map(p => {
            if (typeof p === 'object' && p !== null) {
                return {
                    ...p,
                    pricePerLike: p.pricePerLike !== undefined ? toCents(p.pricePerLike) : undefined,
                    pricePerComment: p.pricePerComment !== undefined ? toCents(p.pricePerComment) : undefined,
                    pricePerShare: p.pricePerShare !== undefined ? toCents(p.pricePerShare) : undefined,
                    pricePerView: p.pricePerView !== undefined ? toCents(p.pricePerView) : undefined,
                };
            }
            return p;
        });
    }

    return processed;
};

// Add the fixed api campaign methods
api.createCampaign = (data) => request('/campaigns', { method: 'POST', body: JSON.stringify(processCampaignData(data)) });
api.updateCampaign = (id, data) => request(`/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(processCampaignData(data)) });
api.deleteCampaign = (id) => request(`/campaigns/${id}`, { method: 'DELETE' });
api.updateCampaignStatus = (id, status) => request(`/campaigns/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
api.joinWaitlist = (campaignId = null) => request('/waitlist', { method: 'POST', body: JSON.stringify({ campaign_id: campaignId }) });
api.getPlatforms = () => request('/platforms');
api.getAvailableCampaigns = (filters = {}) => {
    const processedFilters = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
    if (processedFilters.minBudget) {
        processedFilters.minBudget = Math.round(parseFloat(processedFilters.minBudget) * 100);
    }
    const query = new URLSearchParams(processedFilters).toString();
    return request(`/campaigns/available?${query}`);
};
api.applyToCampaign = (campaignId, data) => request(`/campaigns/${campaignId}/apply`, { method: 'POST', body: JSON.stringify(data) });
api.getMyApplications = () => request('/applications/me');
api.getCampaignApplications = (campaignId) => request(`/campaigns/${campaignId}/applications`);
api.updateApplicationStatus = (id, status, reason) => request(`/applications/${id}/status`, { method: 'PUT', body: JSON.stringify({ status, reason }) });
api.syncApplicationMetrics = (id) => request(`/applications/${id}/sync`, { method: 'POST' });
api.submitWork = (applicationId, data) => request(`/applications/${applicationId}/submit`, { method: 'POST', body: JSON.stringify(data) });

async function parseUploadResponse(response) {
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (response.status === 401) notifyAuthExpired();
        throw new ApiResponseError(extractErrorMessage(data), response.status, data);
    }
    return response.json();
}

api.uploadCampaignMedia = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const csrfToken = getCsrfToken();
    const headers = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

    const response = await fetch(`${API_URL}/upload/campaign-media`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
    });

    return parseUploadResponse(response);
};
api.uploadDraftVideo = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const csrfToken = getCsrfToken();
    const headers = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

    const response = await fetch(`${API_URL}/upload/draft-video`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
    });

    return parseUploadResponse(response);
};
api.approveWork = (applicationId, data) => {
    const payload = {
        ...data,
        amount: data?.amount ? Math.round(parseFloat(data.amount) * 100) : undefined
    };
    return request(`/applications/${applicationId}/approve`, { method: 'POST', body: JSON.stringify(payload) });
};
api.rejectWork = (applicationId, reason) => request(`/applications/${applicationId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });

api.wallet = {
    getWalletBalance: () => request('/wallet'),
    getTransactionHistory: () => request('/wallet/history'),
    createPaymentIntent: (amount) => {
        const amountCents = Math.round(parseFloat(amount) * 100);
        return request('/wallet/create-payment-intent', { method: 'POST', body: JSON.stringify({ amount: amountCents }) });
    },
    confirmPayment: (paymentIntentId) => request('/wallet/confirm-payment', { method: 'POST', body: JSON.stringify({ paymentIntentId }) }),
    onboardWallet: () => request('/wallet/onboard', { method: 'POST' }),
    withdrawWallet: () => request('/wallet/payout', { method: 'POST' }),
};

api.getWalletBalance = api.wallet.getWalletBalance;
api.getTransactionHistory = api.wallet.getTransactionHistory;
api.createPaymentIntent = api.wallet.createPaymentIntent;
api.confirmPayment = api.wallet.confirmPayment;
api.onboardWallet = api.wallet.onboardWallet;
api.withdrawWallet = api.wallet.withdrawWallet;

api.notifications = {
    getNotifications: () => request('/notifications'),
    markNotificationAsRead: (id) => request(`/notifications/${id}/read`, { method: 'PUT' }),
};

api.getNotifications = api.notifications.getNotifications;
api.markNotificationAsRead = api.notifications.markNotificationAsRead;

api.contact = {
    getAll: (status) => request(`/contact${status ? `?status=${status}` : ''}`),
    updateStatus: (id, status) => request(`/contact/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
};

api.support = {
    getMine: () => request('/support/mine'),
    sendMessage: (chatId, body) => request(`/support/chats/${chatId}/messages`, { method: 'POST', body: JSON.stringify({ body }) }),
};

api.conversations = {
    list: () => request('/conversations'),
    get: (id, { before, limit } = {}) => {
        const qs = new URLSearchParams();
        if (before) qs.set('before', before);
        if (limit) qs.set('limit', limit);
        const query = qs.toString();
        return request(`/conversations/${id}${query ? `?${query}` : ''}`);
    },
    getOrCreate: (campaignId, influencerId) =>
        request('/conversations', { method: 'POST', body: JSON.stringify({ campaignId, influencerId }) }),
    sendMessage: (id, body) =>
        request(`/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify({ body }) }),
    markRead: (id) =>
        request(`/conversations/${id}/read`, { method: 'POST' }),
};

api.collaboration = {
    acceptApplication: (applicationId, { acceptNote, agreedAmountCap } = {}) =>
        request(`/applications/${applicationId}/accept`, { method: 'POST', body: JSON.stringify({ acceptNote, agreedAmountCap }) }),
    declineApplication: (applicationId, { reasonCode, otherText } = {}) =>
        request(`/applications/${applicationId}/decline`, { method: 'POST', body: JSON.stringify({ reasonCode, otherText }) }),
    submitCollaboration: (collaborationId, { urls, attestedChecklist, notes } = {}) =>
        request(`/collaborations/${collaborationId}/submit`, { method: 'POST', body: JSON.stringify({ urls, attestedChecklist, notes }) }),
    requestRevision: (collaborationId, { reasonCode, otherText, specificHashtag } = {}) =>
        request(`/collaborations/${collaborationId}/request-revision`, { method: 'POST', body: JSON.stringify({ reasonCode, otherText, specificHashtag }) }),
    approveCollaboration: (collaborationId) =>
        request(`/collaborations/${collaborationId}/approve`, { method: 'POST' }),
    getCollaboration: (collaborationId) =>
        request(`/collaborations/${collaborationId}`),
    getInbox: () =>
        request('/inbox'),
    getStateMachine: () => {
        if (_stateMachineCache !== null) return Promise.resolve(_stateMachineCache);
        return request('/collaborations/meta/state-machine').then((data) => {
            _stateMachineCache = data;
            return data;
        });
    },
    markReceived: (collaborationId) =>
        request(`/collaborations/${collaborationId}/received`, { method: 'POST' }),
    markVisitCompleted: (collaborationId) =>
        request(`/collaborations/${collaborationId}/visit-completed`, { method: 'POST' }),
};

api.getInbox = api.collaboration.getInbox;
api.acceptApplication = api.collaboration.acceptApplication;
api.declineApplication = api.collaboration.declineApplication;
api.requestRevision = api.collaboration.requestRevision;
api.approveCollaboration = api.collaboration.approveCollaboration;
api.getCollaboration = api.collaboration.getCollaboration;
api.submitCollaboration = api.collaboration.submitCollaboration;
api.getStateMachine = api.collaboration.getStateMachine;
api.markReceived = api.collaboration.markReceived;
api.markVisitCompleted = api.collaboration.markVisitCompleted;

api.generic = {
    get: (url) => request(url),
    post: (url, data) => request(url, { method: 'POST', body: JSON.stringify(data) }),
    put: (url, data) => request(url, { method: 'PUT', body: JSON.stringify(data) }),
    patch: (url, data) => request(url, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (url) => request(url, { method: 'DELETE' }),
};

api.get = api.generic.get;
api.post = api.generic.post;
api.put = api.generic.put;
api.patch = api.generic.patch;
api.delete = api.generic.delete;

api.invites = {
    send: (campaignId, influencerId, message, agreedAmountCap) =>
        request('/invites', { method: 'POST', body: JSON.stringify({ campaignId, influencerId, message, agreed_amount_cap: agreedAmountCap }) }),
    getReceived: () => request('/invites/received'),
    getSent: (campaignId) => {
        const qs = campaignId ? `?campaignId=${campaignId}` : '';
        return request(`/invites/sent${qs}`);
    },
    accept: (inviteId) => request(`/invites/${inviteId}/accept`, { method: 'POST' }),
    decline: (inviteId) => request(`/invites/${inviteId}/decline`, { method: 'POST' }),
    updateTerms: (inviteId, agreedAmountCap, message) =>
        request(`/invites/${inviteId}/terms`, { method: 'PUT', body: JSON.stringify({ agreed_amount_cap: agreedAmountCap, message }) }),
};

